import type { paths } from './schema';

/**
 * Typed API client.
 *
 * Every response shape here comes from `schema.d.ts`, generated from the API's
 * committed `openapi.json`. Regenerate with `npm run api:types` after a backend
 * contract change — the build breaks where the two have diverged, which is the
 * whole reason the API declares response schemas.
 */

// Trimmed, and any trailing slash removed: a value pasted into a build
// setting picks up whitespace easily, and the resulting `%20` in the hostname
// surfaces as ERR_NAME_NOT_RESOLVED — which reads as an outage rather than a
// stray character. The website's client carries the same guard.
const BASE = (import.meta.env.VITE_API_URL ?? '/api').trim().replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The access token is held in memory only.
 *
 * Not localStorage: this dashboard reads patient data, and a token in
 * localStorage is readable by any script that manages to run on the page. The
 * refresh cookie is httpOnly and does the work of surviving a reload.
 */
let accessToken: string | null = null;
/** Called with the API's own sentence ("Signed out after inactivity…") when there is one. */
let onUnauthorized: ((reason: string | null) => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setUnauthorizedHandler(fn: ((reason: string | null) => void) | null): void {
  onUnauthorized = fn;
}

/**
 * Background requests do not count as somebody using the dashboard.
 *
 * The API signs a session out after 30 minutes without activity, and every
 * authenticated request used to count as activity — so the bell's 30-second
 * poll kept an unattended tab signed in forever. A request carrying this
 * header never moves the session's last-seen time (the contract agreed with
 * the API; it must also be in the API's CORS allowed headers, or the browser
 * refuses every request that carries it).
 *
 * Marked explicitly: the bell's poll and the timed token refresh, and a 401
 * retry inherits the mark of the request it retries. Otherwise a GET counts
 * as background when nobody has touched the page for a minute — that is what
 * catches a screen's `refetchInterval` (the inbox count, a live meeting, a
 * send's progress) without every hook having to say so. A request a person
 * causes comes within moments of their click or keystroke, and a write is
 * always theirs.
 */
export const BACKGROUND_HEADER = 'X-Enact-Background';
const PERSON_WINDOW_MS = 60_000;
/** The page loading is somebody opening it. */
let lastTouched = Date.now();
if (typeof window !== 'undefined') {
  const touched = () => {
    lastTouched = Date.now();
  };
  for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
    window.addEventListener(type, touched, { capture: true, passive: true });
  }
}
const nobodyThere = (): boolean => Date.now() - lastTouched > PERSON_WINDOW_MS;

/**
 * The keep-alive (20 Oct 2026, QA C-OTH-012). Since the API stopped counting
 * polls and refreshes, a person typing a long note with no other request
 * going out would find their session idled out at 30 minutes. So while
 * somebody is at the page, an ordinary request has to go out now and then.
 *
 * `lastCounted` is when the last request the API counts as activity left —
 * any request not marked background. The keep-alive is due when it was at
 * least `everyMs` ago and a person has touched the page since: an idle tab
 * sends nothing, so it still idles out 30 minutes after the last keystroke,
 * never sooner. AuthContext asks once a minute and, when due, reads
 * GET /auth/me.
 */
let lastCounted = Date.now();
export const keepAliveDue = (everyMs: number): boolean =>
  lastTouched > lastCounted && Date.now() - lastCounted >= everyMs;

/**
 * Refresh in flight, shared by every caller.
 *
 * Without this, a page that fires five queries at once on an expired token
 * would send five refreshes — and since the API rotates on every refresh, four
 * of them would present an already-consumed token and trip reuse detection,
 * logging the user out for doing nothing wrong.
 */
let refreshInFlight: Promise<SessionResponse | null> | null = null;

/**
 * The single refresh path for the whole app.
 *
 * Every caller must come through here — the session boot, the periodic renewal
 * and the 401 retry alike. Two of them refreshing independently present the
 * same cookie, the API rotates on the first and treats the second as reuse, and
 * the whole token family is revoked. StrictMode made that reliable rather than
 * occasional: it double-invokes the boot effect, so a reload signed the user
 * straight back out.
 */
export async function refreshSession(opts: { background?: boolean } = {}): Promise<SessionResponse | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          // The timed renewal, or a poll's retry: keeping the token fresh is
          // not somebody using the dashboard.
          headers: opts.background ? { [BACKGROUND_HEADER]: '1' } : undefined,
        });
        if (!res.ok) return null;
        const body = (await res.json()) as SessionResponse;
        accessToken = body.accessToken;
        return body;
      } catch {
        return null;
      } finally {
        // Cleared on the next tick so concurrent callers all observe the same
        // settled promise before it is discarded.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

async function refresh(background = false): Promise<boolean> {
  return (await refreshSession({ background })) !== null;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /**
   * Send `body` as-is rather than JSON.
   *
   * The REDCap import takes a CSV file, and stringifying it would wrap the
   * whole export in quotes and escape every newline. Set the content type
   * alongside it.
   */
  rawBody?: boolean;
  contentType?: string;
  query?: Record<string, string | number | boolean | undefined>;
  /** Internal: prevents a refresh loop. */
  retrying?: boolean;
  signal?: AbortSignal;
  /**
   * Sent on a timer, not by a person: the API does not count it as activity
   * (see BACKGROUND_HEADER). Left out, a GET decides by whether anyone has
   * touched the page lately; set it to false for a request a person caused.
   */
  background?: boolean;
  /**
   * No session is involved — signing in, asking for a reset link. A 401 here
   * is the answer itself ("That email and password do not match."), not a
   * lapsed token: no refresh, and no "your session ended" on the sign-in form.
   */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  // Decided once, so the retry after a refresh carries the same mark.
  const background = options.background ?? (method === 'GET' && nobodyThere());
  const headers: Record<string, string> = {};
  if (options.body !== undefined) {
    headers['Content-Type'] = options.contentType ?? 'application/json';
  }
  if (accessToken && !options.anonymous) headers.Authorization = `Bearer ${accessToken}`;
  if (background) headers[BACKGROUND_HEADER] = '1';
  else if (!options.anonymous) lastCounted = Date.now();

  const res = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    credentials: 'include',
    body:
      options.body === undefined
        ? undefined
        : options.rawBody
          ? (options.body as BodyInit)
          : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (res.status === 401 && !options.retrying && !options.anonymous) {
    // The access token lives 15 minutes; a 401 usually just means it lapsed.
    if (await refresh(background)) return request<T>(path, { ...options, background, retrying: true });
    // It did not: the session is over (idle, revoked, suspended). The API's
    // own sentence for why goes to the sign-in screen.
    const ended = await apiErrorFrom(res);
    onUnauthorized?.(ended.message);
    throw ended;
  }

  if (!res.ok) throw await apiErrorFrom(res);

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** The API's `{ error: { code, message, details } }`, or plain defaults for a body that is not one. */
async function apiErrorFrom(res: Response): Promise<ApiError> {
  let code = 'error';
  let message = 'Something went wrong.';
  let details: unknown;
  try {
    const body = await res.json();
    code = body?.error?.code ?? code;
    message = body?.error?.message ?? message;
    details = body?.error?.details;
  } catch {
    /* non-JSON error body — keep the defaults */
  }
  return new ApiError(res.status, code, message, details);
}

/** Download a CSV through the authenticated client rather than a bare link. */
export const downloadCsv = (path: string, query: RequestOptions['query'], filename: string): Promise<void> =>
  fetchAndSave(path, query, filename);

/** A file attached to a ticket, through its own route; saved under the name it was attached with. */
export const downloadFile = (path: string, filename: string): Promise<void> => fetchAndSave(path, undefined, filename);

/** GET through the authenticated client and hand the bytes to the browser as a download. */
async function fetchAndSave(path: string, query: RequestOptions['query'], filename: string): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let res = await fetch(buildUrl(path, query), { headers, credentials: 'include' });

  if (res.status === 401 && (await refresh())) {
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    res = await fetch(buildUrl(path, query), { headers, credentials: 'include' });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'error',
      body?.error?.message ?? 'The download failed.',
    );
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * POST for a file and save it. The name comes from the response's
 * Content-Disposition when the API sends one (the API exposes that header to
 * cross-origin reads), else `fallbackName`. Returns the name it saved under.
 */
export async function downloadPost(path: string, body: unknown, fallbackName: string): Promise<string> {
  const send = () => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(buildUrl(path), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
    });
  };

  let res = await send();
  if (res.status === 401 && (await refresh())) res = await send();

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      err?.error?.code ?? 'error',
      err?.error?.message ?? 'The download failed.',
      err?.error?.details,
    );
  }

  const disposition = res.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
  return name;
}

/**
 * Response types, pulled from the generated schema.
 *
 * Note the trailing slashes: Fastify registers a plugin's root route under its
 * prefix as `/submissions/`, and the generated `paths` keys match the document
 * exactly. `Ok<…>` picks the success response — indexing `responses` directly
 * would union the error shapes in with it.
 */
type Ok<
  P extends keyof paths,
  M extends keyof paths[P],
  S extends number = 200,
> = paths[P][M] extends { responses: infer R }
  ? S extends keyof R
    ? R[S] extends { content: { 'application/json': infer B } }
      ? B
      : never
    : never
  : never;

export type MetaResponse = Ok<'/meta/', 'get'>;
export type SubmissionList = Ok<'/submissions/', 'get'>;
export type SubmissionDetail = Ok<'/submissions/{id}', 'get'>;
export type OverviewResponse = Ok<'/submissions/overview', 'get'>;
export type AdminsResponse = Ok<'/admins/', 'get'>;
export type PermissionMatrix = Ok<'/admins/permissions/', 'get'>;
export type AdminPermissions = Ok<'/admins/permissions/{id}', 'get'>;
export type NewsletterResponse = Ok<'/newsletter/', 'get'>;
/** One page of the whole subscriber list, searched by address. */
export type SubscriberList = Ok<'/subscribers/', 'get'>;
export type Subscriber = SubscriberList['subscribers'][number];
export type AnalyticsResponse = Ok<'/analytics/', 'get'>;
export type SessionResponse = Ok<'/auth/login', 'post'>;
export type SendProgress = Ok<'/newsletter/sends/{id}', 'get'>;
/** A draft letter rendered by the API exactly as a reader gets it — nothing stored or sent. */
export type NewsletterPreview = Ok<'/newsletter/preview', 'post'>;
export type NewsletterTestResult = Ok<'/newsletter/test', 'post'>;
export type StudiesResponse = Ok<'/studies/', 'get'>;
export type NamedRolesResponse = Ok<'/admins/roles/', 'get'>;
export type StudyMembersResponse = Ok<'/admins/studies/{slug}/members', 'get'>;
export type RedcapSummaries = Ok<'/redcap/completeness', 'get'>;
export type RedcapCompleteness = Ok<'/redcap/completeness/{slug}', 'get'>;
export type RedcapExtractResult = Ok<'/redcap/completeness/{slug}/extracts', 'post', 201>;
export type RedcapRuleResult = Ok<'/redcap/completeness/{slug}/rules/{instrument}', 'put'>;
export type RedcapProjectResult = Ok<'/redcap/completeness/{slug}/project', 'put'>;
export type RedcapChaseResult = Ok<'/redcap/chases/{id}', 'patch'>;
export type RedcapImportResult = Ok<'/matching/redcap/import', 'post', 201>;
export type RedcapRecordsResponse = Ok<'/matching/redcap', 'get'>;
export type RedcapDictionaryResponse = Ok<'/redcap/studies/{slug}/dictionary', 'get'>;
/** The lab's one REDCap setup (043): every study's project is built the same. */
export type RedcapSetupResponse = Ok<'/redcap/setup/', 'get'>;
export type RedcapMapResponse = Ok<'/redcap/setup/map', 'get'>;
export type RedcapSuggestResponse = Ok<'/redcap/setup/map/suggest', 'post'>;
export type RedcapExportPlanResponse = Ok<'/redcap/studies/{slug}/export', 'get'>;
export type RedcapAnalyticsResponse = Ok<'/redcap/studies/{slug}/analytics', 'get'>;
export type RedcapBoardResponse = Ok<'/redcap/studies/{slug}/board', 'get'>;
export type RedcapKeptFormsResult = Ok<'/redcap/setup/kept-forms', 'put'>;
export type RedcapWeekPage = Ok<'/redcap/completeness/{slug}/week', 'get'>;
export type RedcapWeeks = Ok<'/redcap/weeks', 'get'>;
export type MatchListResponse = Ok<'/matching', 'get'>;
export type MatchCandidatesResponse = Ok<'/matching/candidates', 'get'>;
export type EndorsedOutResponse = Ok<'/submissions/endorsements', 'get'>;
export type ContactListResponse = Ok<'/contacts/', 'get'>;
export type ContactDetailResponse = Ok<'/contacts/{id}', 'get'>;
export type ContactSummaryResponse = Ok<'/contacts/summary', 'get'>;
export type EndorseResult = Ok<'/submissions/{id}/endorse', 'post', 201>;
export type MeetingListResponse = Ok<'/meetings/', 'get'>;
export type MeetingDetailResponse = Ok<'/meetings/{id}', 'get'>;
export type MeetingActionsResponse = Ok<'/meetings/actions', 'get'>;
export type MeetingPeopleResponse = Ok<'/meetings/people', 'get'>;
export type PeopleResponse = Ok<'/admins/people', 'get'>;
export type Person = PeopleResponse['people'][number];
export type MeetingSuggestResponse = Ok<'/meetings/{id}/agenda/suggest', 'post'>;
export type MeetingSummaryDraft = Ok<'/meetings/{id}/summary/draft', 'post'>;
export type MeetingSendResult = Ok<'/meetings/{id}/send', 'post'>;
export type MeetingAskResponse = Ok<'/meetings/{id}/ask', 'post'>;
export type WorkTicketListResponse = Ok<'/work/tickets', 'get'>;
export type WorkTicketDetailResponse = Ok<'/work/tickets/{id}', 'get'>;
export type MessagesResponse = Ok<'/submissions/{id}/messages', 'get'>;
export type SentMessage = Ok<'/submissions/{id}/messages', 'post', 201>;
/** An invite, or a repeat of one: `temporaryPassword` only when the email did not go. */
export type InviteResult = Ok<'/admins/', 'post', 201>;
export type InboxList = Ok<'/inbox/', 'get'>;
export type InboxThreadDetail = Ok<'/inbox/{id}', 'get'>;
export type InboxReplyResult = Ok<'/inbox/{id}/replies', 'post', 201>;
export type InboxCounts = Ok<'/inbox/counts', 'get'>;
/** One conversation in the enact@ inbox (API 051). */
export type InboxThread = InboxList['threads'][number];
export type InboxMessage = InboxThreadDetail['messages'][number];

export type SubmissionRow = SubmissionList['rows'][number];
export type AdminRow = AdminsResponse['admins'][number];
/** None, View-only or Match + View: who may see, and who may make, a REDCap-to-website match. Per person, never by role. */
export type PhiMatch = AdminRow['phiMatch'];
export type PhiMatchLevel = AdminsResponse['phiMatchLevels'][number];
export type NamedRole = NamedRolesResponse['roles'][number];
export type StudyMember = StudyMembersResponse['members'][number];
export type RedcapCell = RedcapCompleteness['cells'][number];
export type RedcapChase = RedcapCompleteness['chases'][number];
export type RedcapWeek = NonNullable<RedcapCompleteness['latest']>;
export type RedcapRuleKey = RedcapCell['rule'];
export type RedcapRecordRows = RedcapCell['recordRows'];
export type RedcapWeekFlag = RedcapWeekPage['flags'][number];
export type RedcapWeekChange = RedcapWeekPage['changes'][number];
export type RedcapRecord = RedcapRecordsResponse['records'][number];
export type RedcapDictionaryField = RedcapDictionaryResponse['fields'][number];
export type RedcapSource = RedcapMapResponse['sources'][number];
export type RedcapFieldStats = RedcapAnalyticsResponse['fields'][number];
/** One card on a board's "Endorsed out" column. */
export type EndorsedOut = EndorsedOutResponse['endorsements'][number];
export type EndorseTarget = MetaResponse['endorseTargets'][number];
export type MeetingRow = MeetingListResponse['rows'][number];
export type Meeting = MeetingDetailResponse['meeting'];
/** One entry on a record's thread: a receipt, a reply or an internal note. */
export type Message = MessagesResponse['messages'][number];
export type MeetingItem = MeetingDetailResponse['items'][number];
export type MeetingPerson = MeetingPeopleResponse['people'][number];
export type MeetingPhase = Meeting['phase'];
export type MeetingItemKind = MeetingItem['kind'];
export type MeetingActionStatus = NonNullable<MeetingItem['status']>;
export type WorkTicket = WorkTicketListResponse['rows'][number];
export type WorkComment = WorkTicketDetailResponse['comments'][number];
/** A file on a ticket (052): what is listed; the bytes come through `downloadFile`. */
export type WorkFile = WorkTicketDetailResponse['files'][number];
export type TicketPriority = WorkTicket['priority'];
export type WorkStatus = WorkTicket['status'];
export type FormKey = SubmissionRow['form'];
export type StageKey = SubmissionRow['stage'];
export type Permission = SessionResponse['admin']['permissions'][number];
